import { useEffect, useState, useRef } from 'react'
import DataGrid from '../data-grid/DataGrid.tsx'
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '../../strings/api-consts.ts'
import { emailMembers, fetchDevices, removeDevices } from '../../reducers/devices/devices.thunk.ts'
import { useAppDispatch } from '../../reducers/store.ts'
import { selectDevices, setDevices } from '../../reducers/devices/devices.slice.ts'
import { useDispatch, useSelector } from 'react-redux'
import { EnrolledDeviceInformation } from '../../types/DeviceTypes.ts'
import { CrossCircledIcon, MagnifyingGlassIcon, PlusCircledIcon } from '@radix-ui/react-icons'
import { Button, IconButton, SegmentedControl, TextField } from '@radix-ui/themes'
import DropDown from '../dropdown/DropDown.tsx'
import { severityOptions, NOT_ACTIVATED, INVITATION, actionOptions } from '../../strings/dropdown-options.ts'
import toast from 'react-hot-toast'
import { notActivatedDeviceColumns, deviceColumns } from './DeviceColumns.tsx'
import AddDeviceModal from './AddDeviceModal.tsx'
import DeviceRemoveModal from './DeviceRemoveModal.tsx'
import { SelectOption } from '../../types/DropDownTypes.ts'
import checkIcon from '../../assets/check_circle.svg'
import PageSkeleton from '../skeleton/PageSkeleton.tsx'
import { Pagination } from '../pagination/Pagination.tsx'
import { PaginationSelectOption } from '../../types/MemberTypes.ts'
import { useNavigate } from 'react-router-dom'
import { selectUser } from '../../reducers/users'
import { inPov } from '../../utils/session.ts'
import { HEADER_HEIGHT_WITH_TRIAL, HEADER_HEIGHT_WITHOUT_TRIAL } from '../../strings/consts.ts'
import { DEFAULT_TOAST_CONFIG } from '../toast/consts.ts'

function DevicesWrapper() {
  const di = useAppDispatch()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { session } = useSelector(selectUser)
  const { devices, totalPages, deviceTableCurrentPage } = useSelector(selectDevices)
  const [severityFilter, setSeverityFilter] = useState<SelectOption[]>([])
  const [actionOption, setActionOption] = useState<SelectOption | null>(null)
  const [clickedInputs, setClickedInputs] = useState<EnrolledDeviceInformation[]>([])
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>({})
  const [showRemoveDeviceModal, setShowRemoveDeviceModal] = useState<boolean>(false)
  const [dataLoading, setDataLoading] = useState<boolean>(true)
  const [platformToShow, setPlatformToShow] = useState<string>('enrolled')
  const [osToShow, setOsToShow] = useState<string>('all')
  const [navigateToDevicePage, setNavigateToDevicePage] = useState<boolean>(false)
  const [popoutData, setPopoutData] = useState<EnrolledDeviceInformation | null>(null)
  const [showAddDeviceModal, setShowAddDeviceModal] = useState<boolean>(false)
  const [nameFilter, setNameFilter] = useState<string>('')

  const userHasSortedRef = useRef(false)
  const defaultSortOrder = 'desc'
  const defaultSortColumnEnrolled = 'hasOpenAlerts'
  const defaultSortColumnNotActivated = 'createdAt'
  const [sortOrder, setSortOrder] = useState<string>(defaultSortOrder)
  const [sortColumn, setSortColumn] = useState<string>(defaultSortColumnEnrolled)

  useEffect(() => {
    let effectiveSortColumn = sortColumn
    let effectiveSortOrder = sortOrder

    if (!userHasSortedRef.current) {
      effectiveSortColumn = platformToShow === 'enrolled' ? defaultSortColumnEnrolled : defaultSortColumnNotActivated
      effectiveSortOrder = 'desc'
      setSortColumn(effectiveSortColumn)
      setSortOrder(effectiveSortOrder)
    }

    setDataLoading(true)
    di(
      fetchDevices(
        DEFAULT_PAGE,
        DEFAULT_PAGE_SIZE,
        severityFilter,
        platformToShow,
        nameFilter,
        effectiveSortOrder,
        effectiveSortColumn,
        osToShow
      )
    )
  }, [severityFilter, osToShow])

  useEffect(() => {
    const column = platformToShow === 'enrolled' ? defaultSortColumnEnrolled : defaultSortColumnNotActivated
    const order = defaultSortOrder
    setSortColumn(column)
    setSortOrder(order)
    setDataLoading(true)
    di(
      fetchDevices(DEFAULT_PAGE, DEFAULT_PAGE_SIZE, severityFilter, platformToShow, nameFilter, order, column, osToShow)
    )
  }, [platformToShow])

  const sortBy = (column: string) => {
    userHasSortedRef.current = true
    let newSortColumn = sortColumn
    let newSortOrder = sortOrder

    const defaultSortColumn = platformToShow === 'enrolled' ? defaultSortColumnEnrolled : defaultSortColumnNotActivated
    if (sortColumn === column) {
      if (sortOrder === 'desc' && column !== defaultSortColumn) {
        newSortColumn = defaultSortColumn
        newSortOrder = defaultSortOrder
      } else {
        newSortOrder = sortOrder === 'desc' ? 'asc' : 'desc'
      }
    } else {
      newSortColumn = column
      newSortOrder = 'asc'
    }

    setSortColumn(newSortColumn)
    setSortOrder(newSortOrder)

    setDataLoading(true)
    di(
      fetchDevices(
        DEFAULT_PAGE,
        DEFAULT_PAGE_SIZE,
        severityFilter,
        platformToShow,
        nameFilter,
        newSortOrder,
        newSortColumn,
        osToShow
      )
    )
  }

  const handlePageClick = (data: PaginationSelectOption) => {
    setDataLoading(true)
    di(
      fetchDevices(
        data.selected + 1,
        DEFAULT_PAGE_SIZE,
        severityFilter,
        platformToShow,
        nameFilter,
        sortOrder,
        sortColumn,
        osToShow
      )
    )
  }

  const handleCheckboxChange = (accessCode: string) => {
    const selectedItem = devices!.find((item) => item.accessCode === accessCode)!
    if (clickedInputs.includes(selectedItem)) {
      setClickedInputs(clickedInputs.filter((item) => item.accessCode !== accessCode))
    } else {
      setClickedInputs([...clickedInputs, selectedItem])
    }
  }

  const handleAction = async () => {
    const deviceCodes = clickedInputs.map((row: EnrolledDeviceInformation) => row.accessCode)
    if (actionOption!.value === INVITATION) {
      const count = await di(emailMembers(deviceCodes))
      count
        ? toast.success(
            `Invitations are being sent to ${count} organization member${count === '1' ? '' : 's'}`,
            DEFAULT_TOAST_CONFIG
          )
        : resetTable()
      resetTable()
    }
    if (actionOption!.value === NOT_ACTIVATED) {
      setShowRemoveDeviceModal(true)
    }
  }

  const resetTable = () => {
    setCheckedItems({})
    setClickedInputs([])
    setActionOption(null)
  }

  const removeUserDevices = async () => {
    const deviceCodes = clickedInputs.map((row: EnrolledDeviceInformation) => row.accessCode)
    const removedSuccessfully = await di(removeDevices(deviceCodes))
    if (removedSuccessfully) {
      setDataLoading(true)
      await di(
        fetchDevices(
          DEFAULT_PAGE,
          DEFAULT_PAGE_SIZE,
          severityFilter,
          platformToShow,
          nameFilter,
          sortOrder,
          sortColumn,
          osToShow
        )
      )
      showSuccessfulRemoval(deviceCodes)
    } else {
      toast.error('An error occurred while removing the device(s)', DEFAULT_TOAST_CONFIG)
    }
  }

  const showSuccessfulRemoval = (deviceCodes: string[]) => {
    toast.success(
      `${deviceCodes.length} device${deviceCodes.length === 1 ? ' was' : 's were'} successfully removed`,
      DEFAULT_TOAST_CONFIG
    )
    resetTable()
    setShowRemoveDeviceModal(false)
  }

  useEffect(() => {
    if (devices || (devices && (devices as EnrolledDeviceInformation[]).length === 0)) {
      setDataLoading(false)
    } else if (!devices) {
      setDataLoading(true)
    }
  }, [devices])

  useEffect(() => {
    return () => {
      dispatch(setDevices(undefined))
    }
  }, [])

  const searchName = () => {
    setDataLoading(true)
    di(
      fetchDevices(
        DEFAULT_PAGE,
        DEFAULT_PAGE_SIZE,
        severityFilter,
        platformToShow,
        nameFilter,
        sortOrder,
        sortColumn,
        osToShow
      )
    )
  }

  const clearNameFilter = () => {
    setDataLoading(true)
    di(
      fetchDevices(DEFAULT_PAGE, DEFAULT_PAGE_SIZE, severityFilter, platformToShow, '', sortOrder, sortColumn, osToShow)
    )
  }

  const configureColumns = () => {
    switch (platformToShow) {
      case 'enrolled':
        return deviceColumns(
          checkedItems,
          setCheckedItems,
          handleCheckboxChange,
          setNavigateToDevicePage,
          setPopoutData,
          session?.portalPreferences?.vpnScanning ?? false
        )
      default:
        return notActivatedDeviceColumns(checkedItems, setCheckedItems, handleCheckboxChange)
    }
  }

  useEffect(() => {
    if (navigateToDevicePage) {
      navigate(`/devices/${popoutData?.identifier}`)
    }
  }, [navigateToDevicePage])

  return (
    <>
      <div
        className="absolute top-18 left-52 rounded-tl-lg border-mainBorder border-t border-l bg-contentBackground w-[calc(100%_-_208px)] p-9"
        style={{
          height: inPov(session)
            ? `calc(100% - ${HEADER_HEIGHT_WITH_TRIAL})`
            : `calc(100% - ${HEADER_HEIGHT_WITHOUT_TRIAL})`,
        }}
      >
        {dataLoading && (
          <div className="flex justify-center items-center min-h-screen">
            <PageSkeleton />
          </div>
        )}
        <div className="mb-6 flex w-full">
          <SegmentedControl.Root
            defaultValue="enrolled"
            value={platformToShow}
            className="h-[38px] mr-4 bg-controllerBackground text-controllerText"
            onValueChange={(newValue: string) => setPlatformToShow(newValue)}
          >
            <SegmentedControl.Item value="enrolled">Enrolled</SegmentedControl.Item>
            <SegmentedControl.Item value="pending">Pending</SegmentedControl.Item>
          </SegmentedControl.Root>

          {platformToShow === 'enrolled' && (
            <SegmentedControl.Root
              defaultValue="all"
              value={osToShow}
              className="h-[38px] mr-4 bg-controllerBackground text-controllerText"
              onValueChange={(newValue: string) => setOsToShow(newValue)}
            >
              <SegmentedControl.Item value="all">All</SegmentedControl.Item>
              <SegmentedControl.Item value="ios">iOS</SegmentedControl.Item>
              <SegmentedControl.Item value="android">Android</SegmentedControl.Item>
            </SegmentedControl.Root>
          )}

          <TextField.Root
            value={nameFilter}
            placeholder="Search..."
            className="w-[200px] h-[38px] mr-4 bg-inputBackground text-mainText dark:shadow-none border border-transparent dark:border-neutral-11 hover:shadow-none hover:border hover:border-borderPurple hover:dark:border-borderPurple focus:ring-mainBorder focus:ring-borderPurple p-0"
            onChange={(e) => {
              setNameFilter(e.target.value)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                searchName()
              }
            }}
          >
            <TextField.Slot className="p-0"></TextField.Slot>
            {nameFilter !== '' && (
              <TextField.Slot>
                <IconButton
                  size="1"
                  variant="ghost"
                  onClick={() => {
                    setNameFilter('')
                    clearNameFilter()
                  }}
                >
                  <CrossCircledIcon height="14" width="14" className="cursor-pointer dark:text-white" />
                </IconButton>
              </TextField.Slot>
            )}
            <TextField.Slot>
              <IconButton
                size="1"
                variant="ghost"
                className="cursor-pointer dark:text-white"
                onClick={() => searchName()}
              >
                <MagnifyingGlassIcon height="14" width="14" />
              </IconButton>
            </TextField.Slot>
          </TextField.Root>
          {platformToShow === 'enrolled' && (
            <DropDown
              value={severityFilter}
              onChangeHandler={setSeverityFilter}
              options={severityOptions}
              style="z-10 min-w-[160px]"
              placeholder="Threat Status"
              multiple={true}
              disabled={false}
            />
          )}
          <div className="ml-auto flex">
            <DropDown
              value={actionOption}
              onChangeHandler={setActionOption}
              options={actionOptions}
              style="z-10 max-w-[300px]"
              placeholder={clickedInputs.length === 0 ? 'Select Devices' : 'Select Option'}
              multiple={false}
              disabled={clickedInputs.length === 0 ? true : false}
            />
            {clickedInputs.length > 0 && (
              <button
                onClick={handleAction}
                disabled={!actionOption}
                className="h-[38px] ml-4 rounded px-3 text-mainPurple dark:text-secondaryPurple border border-mainPurple dark:border-secondaryPurple cursor-pointer"
              >
                Send
              </button>
            )}
            <Button className="bg-mainPurple hover:bg-tertiaryPurple active:active:bg-tertiaryPurple h-[38px] ml-4 cursor-pointer" onClick={() => setShowAddDeviceModal(true)}>
              <PlusCircledIcon /> Add Devices
            </Button>
          </div>
        </div>
        {devices && devices.length > 0 && (
          <>
            <DataGrid
              columns={configureColumns()}
              sortFunction={(column: string) => {
                sortBy(column)
              }}
              sortColumn={sortColumn}
              sortOrder={sortOrder}
              data={devices}
              loading={dataLoading}
              classNames={inPov(session) ? 'max-h-[calc(100vh_-_276px)]' : ' max-h-[calc(100vh_-_240px)]'}
              lastItemClassName={`${platformToShow !== 'enrolled' ? (sortColumn == defaultSortColumnNotActivated ? 'justify-end pr-[70px]' : 'justify-end pr-[80px]') : ''}`}
            />
            <div className="mt-2">
              <Pagination
                totalPages={totalPages}
                forcePage={deviceTableCurrentPage}
                handlePageClick={handlePageClick}
              />
            </div>
          </>
        )}
        {devices && devices.length === 0 && (
          <div className="block items-center mt-20">
            <div className="flex items-center justify-center">
              <img src={checkIcon} alt="No devices found" className="h-12 w-12" />
            </div>
            <p className="text-center font-bold">No Devices Found</p>
            <p className="text-center text-sm">Add devices above or refine your search</p>
          </div>
        )}
      </div>
      {showRemoveDeviceModal && (
        <DeviceRemoveModal
          clickedInputs={clickedInputs}
          setShowRemoveDeviceModal={setShowRemoveDeviceModal}
          removeUserDevices={removeUserDevices}
        />
      )}
      {showAddDeviceModal && (
        <AddDeviceModal setShowAddDeviceModal={setShowAddDeviceModal} setPlatformToShow={setPlatformToShow} />
      )}
    </>
  )
}

export default DevicesWrapper
